import { useEffect, useRef, useState } from 'react'
import { LinkIcon, LockIcon, StickyNoteIcon } from 'lucide-react'
import { ConnectorPicker } from './connector-picker'
import { DEFAULT_SIZES, type ConnectorProvider } from './item-defaults'
import type { Point } from './geometry'

// An unpersisted, auto-focused card the user types into before it becomes a real item.
// Placing a Note / Instruction / Link / GitHub connector drops one of these instead of
// opening a dialog; it commits to a real item on blur/Enter/pick or is discarded if empty.
export type Draft =
  | { id: string; kind: 'text'; textKind: 'reference' | 'instruction'; position: Point }
  | { id: string; kind: 'url'; position: Point }
  | { id: string; kind: 'connector'; provider: ConnectorProvider; position: Point }

type Props = {
  draft: Draft
  // Called with the committed value: the note text, or the full https:// URL.
  onCommit: (value: string) => void
  onCancel: () => void
  /** Connector targets already on the board, so the picker can mark them as added. */
  existingConnectorTargets?: string[]
}

export function DraftCard({ draft, onCommit, onCancel, existingConnectorTargets = [] }: Props) {
  const size =
    draft.kind === 'text'
      ? DEFAULT_SIZES.text
      : draft.kind === 'connector'
        ? DEFAULT_SIZES.connectorDraft
        : DEFAULT_SIZES.url
  return (
    <div
      className="absolute"
      style={{ left: draft.position.x, top: draft.position.y, width: size.w, height: size.h }}
      // Keep pointer/keys off the canvas (no marquee/pan/shortcuts while typing).
      onPointerDown={(e) => e.stopPropagation()}>
      {draft.kind === 'text' ? (
        <DraftTextCard draft={draft} onCommit={onCommit} onCancel={onCancel} />
      ) : draft.kind === 'url' ? (
        <DraftUrlCard onCommit={onCommit} onCancel={onCancel} />
      ) : (
        <ConnectorPicker
          provider={draft.provider}
          existingTargets={existingConnectorTargets}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      )}
    </div>
  )
}

function DraftTextCard({
  draft,
  onCommit,
  onCancel,
}: {
  draft: Extract<Draft, { kind: 'text' }>
  onCommit: (value: string) => void
  onCancel: () => void
}) {
  const [text, setText] = useState('')
  const ref = useRef<HTMLTextAreaElement>(null)
  const isInstruction = draft.textKind === 'instruction'

  useEffect(() => {
    // rAF so focus lands after the card paints (matches sticky-note's edit focus).
    const raf = requestAnimationFrame(() => ref.current?.focus())
    return () => cancelAnimationFrame(raf)
  }, [])

  const commit = () => {
    const next = text.trim()
    if (next) onCommit(next)
    else onCancel()
  }

  return (
    <div className="ring-primary ring-offset-background bg-card text-foreground relative h-full w-full overflow-hidden rounded-lg p-4 shadow-lg ring-2 ring-offset-2">
      <p className="text-foreground mb-2 flex items-center gap-1 text-sm font-medium">
        {isInstruction ? <LockIcon size={12} /> : <StickyNoteIcon size={12} />}
        {isInstruction ? 'Instruction' : 'Note'}
      </p>
      <textarea
        ref={ref}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Escape') onCancel()
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit()
        }}
        placeholder={
          isInstruction ? 'Never mention…  (⌘⏎ to save)' : 'About this brand…  (⌘⏎ to save)'
        }
        className="text-foreground placeholder:text-muted-foreground h-[calc(100%-1.75rem)] w-full resize-none bg-transparent text-sm leading-relaxed outline-none"
      />
    </div>
  )
}

function DraftUrlCard({
  onCommit,
  onCancel,
}: {
  onCommit: (value: string) => void
  onCancel: () => void
}) {
  // Same scheme-stripping / validation as the old Add-link dialog: the user types the part
  // after `https://`, which is rendered as a static prefix.
  const [tail, setTail] = useState('')
  const ref = useRef<HTMLInputElement>(null)
  const fullUrl = `https://${tail.trim()}`
  const isValid = (() => {
    if (!tail.trim()) return false
    try {
      return new URL(fullUrl).hostname.length > 0
    } catch {
      return false
    }
  })()

  useEffect(() => {
    const raf = requestAnimationFrame(() => ref.current?.focus())
    return () => cancelAnimationFrame(raf)
  }, [])

  const commit = () => {
    if (isValid) onCommit(fullUrl)
    else onCancel()
  }

  return (
    <div className="ring-primary ring-offset-background bg-card text-foreground relative flex h-full w-full flex-col gap-2 overflow-hidden rounded-lg p-4 shadow-lg ring-2 ring-offset-2">
      <p className="text-foreground flex items-center gap-1 text-sm font-medium">
        <LinkIcon size={12} />
        Link
      </p>
      <div className="border-input bg-background focus-within:border-ring focus-within:ring-ring/50 flex items-center rounded-md border px-3 py-2 text-sm transition-colors focus-within:ring-[3px]">
        <span className="text-muted-foreground select-none">https://</span>
        <input
          ref={ref}
          value={tail}
          onChange={(e) =>
            setTail(e.target.value.replace(/^\s*https?:\/\//i, '').replace(/^\/+/, ''))
          }
          onBlur={commit}
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Escape') onCancel()
            if (e.key === 'Enter') commit()
          }}
          placeholder="example.com"
          className="text-foreground placeholder:text-muted-foreground ml-0.5 w-full flex-1 bg-transparent outline-none"
        />
      </div>
      <p className="text-muted-foreground text-[11px] leading-snug">
        Scraped for content when you run the analyzer. Enter to add.
      </p>
    </div>
  )
}
